import { useQuery } from '@tanstack/react-query';
import Checkbox from '@ui/Checkbox';
import { updateOrderByKey } from '@util/firestore/orders';
import { getStripeCustomer } from '@util/firestore/payments';
import { getUserById, getUsersByIds } from '@util/firestore/users';
import { useAuth } from 'context/AuthContext';
import { OrderDocument } from 'models/order';
import { UserDocument } from 'models/user';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

export default function ContactCheckList({ order }: { order: OrderDocument }) {
  // ADMIN ONLY
  const router = useRouter();
  const { userDoc } = useAuth();
  const { data: buyerDoc } = useQuery({
    queryKey: ['user', order.buyer_id],
    queryFn: () => getUserById(order.buyer_id),
    enabled: !!order.buyer_id && userDoc?.roles?.admin,
  });
  const { data: sellers } = useQuery({
    queryKey: ['sellers', order.seller_arr?.join(', ')],
    queryFn: () => getUsersByIds(order.seller_arr),
    enabled: !!order.buyer_id && userDoc?.roles?.admin,
  });

  const { data: buyerStripeAccount } = useQuery({
    queryKey: ['stripeAccount', order.buyer_id],
    queryFn: () => getStripeCustomer(order.buyer_id),
    enabled: !!order.buyer_id && !!buyerDoc?.customer_id,
  });

  const getCheckList = (user: UserDocument) => {
    return order.contact_checklist?.find(
      (checklist) => checklist.uid === user.uid
    );
  };

  // const { data: twilioMessages } = useQuery({
  //   queryKey: ['twilioMessages', order.id],
  //   queryFn: () =>
  //     getMessagesForPhoneNumber(
  //       sellers?.map((seller) => seller.phone),
  //       order
  //     ),
  //   enabled: !!order.id && !!sellers,
  // });

  const updateCheckList = async (
    user: UserDocument,
    type: 'email' | 'phone'
  ) => {
    let newChecklist = order.contact_checklist?.map((checklist) => {
      if (checklist.uid === user.uid) {
        return { ...checklist, [type]: !checklist[type] };
      }
      return checklist;
    });
    if (!newChecklist) {
      newChecklist = [{ uid: user.uid, [type]: true }];
    }
    if (!newChecklist.find((checklist) => checklist.uid === user.uid)) {
      newChecklist.push({ uid: user.uid, [type]: true });
    }
    await updateOrderByKey(order.id, 'contact_checklist', newChecklist);
    router.refresh();
  };
  return (
    <div className="flex w-full flex-col gap-[1.6rem] rounded-brand border-2 p-[1.6rem] shadow-lg">
      <h3 className="text-[2.4rem] font-semibold">Contact Checklist</h3>
      <div className="flex flex-col">
        <h4 className="text-medium text-[1.8rem] font-semibold text-gray-500">
          Buyer
        </h4>
        {buyerDoc && (
          <div className="grid grid-cols-3">
            <Link
              className="underline"
              target="_blank"
              href={`https://console.firebase.google.com/u/0/project/${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}/firestore/data/~2Fusers~2F${buyerDoc.uid}`}
            >
              {buyerDoc.username}
            </Link>

            <Checkbox
              selected={getCheckList(buyerDoc)?.email}
              label={'Email'}
              onChange={() => {
                updateCheckList(buyerDoc, 'email');
              }}
            />
            <Checkbox
              selected={getCheckList(buyerDoc)?.phone}
              label={'Dialpad'}
              onChange={() => {
                updateCheckList(buyerDoc, 'phone');
              }}
            />
            <div className="flex flex-col">
              <span>
                Phone: {buyerDoc.phone ? buyerDoc.phone : order.address.phone}
              </span>
              <span>Name: {buyerStripeAccount?.name}</span>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col">
        <h4 className="text-medium text-[1.8rem] font-semibold text-gray-500">
          Sellers
        </h4>
        {sellers?.map((seller) => (
          <div className="flex w-full flex-col gap-[1.6rem]" key={seller.uid}>
            <div className="grid grid-cols-3">
              <Link
                className="underline"
                target="_blank"
                href={`https://console.firebase.google.com/u/0/project/${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}/firestore/data/~2Fusers~2F${seller.uid}`}
              >
                {seller.username}
              </Link>
              <Checkbox
                label={'Email'}
                selected={getCheckList(seller)?.email}
                onChange={() => updateCheckList(seller, 'email')}
              />
              <Checkbox
                selected={getCheckList(seller)?.phone}
                label={'Dialpad'}
                onChange={() => updateCheckList(seller, 'phone')}
              />
              <div className="flex flex-col">
                <span>Phone: {seller.phone && seller.phone}</span>
              </div>
            </div>
            {/* <div className="flex max-h-[50rem] flex-col gap-[1.6rem] overflow-y-auto">
              <h3 className="text-[1.8rem] font-semibold">Twilio Messages</h3>
              {!!twilioMessages?.[seller.phone]?.length
                ? twilioMessages[seller.phone].map((msg) => (
                    <span key={msg}>{msg}</span>
                  ))
                : 'No messages found in Twilio'}
            </div> */}
          </div>
        ))}
      </div>
    </div>
  );
}
